import {
  Modal,
  Box,
  Typography,
  Button,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextareaAutosize,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { updateGolfAreaSpotById } from '../../../api/golf';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';
import { useSelector } from 'react-redux';

const AreaSpotSettingModal = ({ open, handleClose, widgetData, refreshSpotAreas }) => {
  const { t } = useTranslation();
  const { control, reset, setValue, getValues } = useForm();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const [spotNumber, setSpotNumber] = useState(widgetData?.spotNumber || null);
  const [spotName, setSpotName] = useState(widgetData?.spotName || '');
  const [spotStatus, setSpotStatus] = useState(widgetData?.spotStatus || '');
  const [appReservationEnable, setAppReservationEnable] = useState(widgetData?.appReservationEnable || '');
  const [spotMemo, setSpotMemo] = useState(widgetData?.spotMemo || '');

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '465px',
    height: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  useEffect(() => {
    if (widgetData) {
      setValue('areaName', widgetData.areaName || '');
      setValue('spotName', widgetData.spotName || '');
      setValue('spotNumber', widgetData.spotNumber || '');
      setValue('spotMemo', widgetData.spotMemo || '');
      setSpotStatus(widgetData.spotStatus || '');

      setAppReservationEnable(widgetData.appReservationEnable || '');

      setSpotNumber(widgetData.spotNumber || '');
      setSpotName(widgetData.spotName || '');
      setSpotMemo(widgetData.spotMemo || '');
    }
  }, [widgetData, setValue]);

  const onChangeSpotName = e => {
    const value = e.target.value;
    // 최대 6글자로 제한
    if (value.length <= 6) {
      setSpotName(value);
      setValue('spotName', value);
    }
  };

  const onChangeSpotStatus = e => {
    const value = e.target.value;
    setSpotStatus(value);
    setValue('spotStatus', value);
  };

  const onChangeAppReservationEnable = e => {
    const value = e.target.value;
    setAppReservationEnable(value);
    setValue('appReservationEnable', value);
  };

  const onChangeSpotMemo = e => {
    const value = e.target.value;
    setSpotMemo(value);
    setValue('spotMemo', value);
  };

  const handleSubmit = async () => {
    const data = getValues();
    const formData = {
      ...data,
      spotStatus: spotStatus || data.spotStatus,
      appReservationEnable: appReservationEnable || data.appReservationEnable,
      spotMemo: spotMemo || data.spotMemo,
      spotNumber: spotNumber || data.spotNumber,
      spotName: spotName || data.spotName,
    };

    try {
      const response = await updateGolfAreaSpotById(user.token, widgetData.id, formData);
      handleClose();
      reset();
      if (refreshSpotAreas) {
        await refreshSpotAreas();
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={modalStyle}>
        <Box
          sx={{
            height: '56px',
            backgroundColor: '#262626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: '0 32px',
          }}>
          <Typography
            id="modal-modal-title"
            sx={{
              display: 'flex',
              flexDirection: 'tow',
              justifyContent: 'space-between',
              color: '#fff',
              fontSize: '20px',
              fontWeight: '600',
            }}>
            <Box sx={{ fontSize: '18px' }}>{t('Spot detail setting')}</Box>
          </Typography>
          <CloseIcon sx={{ color: '#fff', width: '32px', height: '32px', cursor: 'pointer' }} onClick={handleClose} />
        </Box>
        <Box
          sx={{
            minHeight: '295px',
            p: '32px 32px',
            display: 'flex',
            flexDirection: 'column',
            justifyItems: 'start',
            gap: '16px',
          }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot area')}</Typography>
            <FormInputText control={control} name="areaName" rules={{ required: true }} disabled={true} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot number')}</Typography>
            {/* 스팟 번호는 배치(행·열)가 정하고 백엔드도 반영하지 않는다 — 편집 불가 */}
            <FormInputText
              control={control}
              name="spotNumber"
              placeholder={widgetData?.spotNumber != null ? String(widgetData.spotNumber) : t('Spot number')}
              disabled={true}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot name')}</Typography>
            <FormInputText
              control={control}
              name="spotName"
              placeholder={widgetData?.spotName || t('Spot name')}
              rules={{ required: true, maxLength: 6 }}
              onChange={onChangeSpotName}
              inputProps={{ maxLength: 6 }}
            />
            <Typography sx={{ fontSize: '12px', color: '#EE735A', mt: '4px' }}>
              {t('Maximum 6 characters can be entered')}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot status')}</Typography>
            <Box>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="spot-status-buttons-group"
                  value={spotStatus}
                  onChange={onChangeSpotStatus}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px',
                  }}>
                  <FormControlLabel value="READY" control={<Radio />} label={t('In use')} />
                  <FormControlLabel value="SUSPENDED" control={<Radio />} label={t('Waiting for use')} />
                  <FormControlLabel value="DISABLED" control={<Radio />} label={t('Unusable')} />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
              {t('App reservation status')}
            </Typography>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                name="app-reservation-status-buttons-group"
                value={appReservationEnable}
                onChange={onChangeAppReservationEnable}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                }}>
                <FormControlLabel value="Y" control={<Radio />} label={t('Possible')} />
                <FormControlLabel value="N" control={<Radio />} label={t('Impossible')} />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot memo')}</Typography>
            <TextareaAutosize
              placeholder={t('Spot memo')}
              minRows={8}
              value={spotMemo}
              onChange={onChangeSpotMemo}
              style={{
                width: '100%',
                padding: '15px',
                borderRadius: '4px',
                height: '100px',
                borderColor: 'rgba(133, 133, 133, 0.5)',
              }}
            />
          </Box>
          <Box
            sx={{
              height: '80px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
            }}>
            <Button
              sx={{
                backgroundColor: '#D9D9D9',
                width: '120px',
                height: '48px',
                borderRadius: '16px',
                fontSize: '20px',
                color: '#000',
                '&:hover': { backgroundColor: '#A6A6A6' },
              }}
              onClick={() => {
                handleClose();
                reset();
              }}>
              {t('Cancel')}
            </Button>
            <Button
              sx={{
                backgroundColor: '#58B9C1',
                width: '240px',
                height: '48px',
                borderRadius: '16px',
                fontSize: '20px',
                color: '#fff',
                '&:hover': { backgroundColor: '#307C82' },
              }}
              onClick={handleSubmit}>
              {t('Setting completed')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AreaSpotSettingModal;
